import * as React from 'react';
import { Check, ChevronsUpDown, Loader2, X } from 'lucide-react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/command';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { cn } from '@Utils/cn';

type ComboboxItem = {
  value: string;
  label: string;
};

type ComboboxGroup = {
  label: string;
  items: ComboboxItem[];
};

interface GroupedMultiSelectComboboxProps {
  groups: ComboboxGroup[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  maxSelected?: number;
  className?: string;
  inModal?: boolean;
}

export function GroupedMultiSelectCombobox({
  groups,
  selectedValues,
  onChange,
  placeholder = 'Select items...',
  disabled,
  loading = false,
  maxSelected,
  className,
  inModal,
}: GroupedMultiSelectComboboxProps) {
  const [open, setOpen] = React.useState(false);

  const byValue = React.useMemo(() => {
    const m = new Map<string, ComboboxItem>();
    groups.forEach((group) => {
      group.items.forEach((item) => m.set(item.value, item));
    });
    return m;
  }, [groups]);

  const selectedItems: ComboboxItem[] = (selectedValues ?? [])
    .map((val) => byValue.get(val))
    .filter(Boolean) as ComboboxItem[];

  const selectedItemsByGroup = React.useMemo(() => {
    const grouped = new Map<string, ComboboxItem[]>();
    groups.forEach((group) => {
      const items = group.items.filter((item) =>
        selectedValues.includes(item.value),
      );
      if (items.length > 0) {
        grouped.set(group.label, items);
      }
    });
    return grouped;
  }, [groups, selectedValues]);

  const toggle = (value: string) => {
    const exists = selectedValues.includes(value);
    if (exists) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      if (maxSelected && selectedValues.length >= maxSelected) return;
      onChange([...selectedValues, value]);
    }
  };

  const clearAll = () => onChange([]);

  const totalItems = groups.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <div className={cn('space-y-2', className)}>
      <Popover open={open} onOpenChange={setOpen} modal={inModal}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'w-full justify-between',
              !selectedItems.length && 'text-muted-foreground',
            )}
            disabled={disabled || loading}
          >
            <span className="truncate">
              {loading
                ? 'Loading...'
                : selectedItems.length
                  ? `${selectedItems.length} selected`
                  : placeholder}
            </span>
            <span className="inline-flex items-center gap-1">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
          <Command shouldFilter>
            <CommandInput placeholder="Search..." />
            <CommandList>
              <CommandEmpty>
                {loading ? (
                  <div className="flex w-full items-center justify-center p-4">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                  </div>
                ) : (
                  'No items found.'
                )}
              </CommandEmpty>

              {selectedItems.length > 0 && (
                <CommandGroup>
                  <CommandItem
                    value="__clear__"
                    className="text-muted-foreground"
                    onSelect={() => clearAll()}
                  >
                    Clear all
                  </CommandItem>
                </CommandGroup>
              )}

              {!loading &&
                totalItems > 0 &&
                groups.map((group) => (
                  <CommandGroup key={group.label} heading={group.label}>
                    {group.items.map((item) => {
                      const selected = selectedValues.includes(item.value);
                      return (
                        <CommandItem
                          key={item.value}
                          value={`${item.value}|${item.label}`}
                          onSelect={() => toggle(item.value)}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              selected ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          <span className="truncate">{item.label}</span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedItems.length > 0 ? (
        <div className="space-y-3">
          {Array.from(selectedItemsByGroup.entries()).map(
            ([groupLabel, items]) => (
              <div key={groupLabel}>
                <div className="text-muted-foreground mb-1.5 text-xs font-medium">
                  {groupLabel}
                </div>
                <div className="flex flex-wrap gap-2">
                  {items.map((item) => (
                    <span
                      key={item.value}
                      className="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs"
                    >
                      {item.label}
                      <button
                        type="button"
                        aria-label={`Remove ${item.label}`}
                        onClick={() => toggle(item.value)}
                        className="hover:bg-muted rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}
